import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import MainLayout from "Layouts/MainLayout/MainLayout";
import { Button } from "Components/Common/Button/Button";
import { Toggle } from "Components/Common/ToggleBtn/Toggle";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ApiError } from "Components/Common/ApiError";
import ModalBasic from "Components/Modal/Modal";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ReactComponent as BarsIcon } from "Assets/Bars.svg";
import { useDeleteVisitType, useReorderVisitTypes, useSetVisitTypeActive, useVisitTypes, visitTypeKeys } from "CustomHooks/useVisitTypes";
import { VISIT_TYPES } from "Constants/Views";
import { AddVisitTypeModal } from "./AddVisitTypeModal";

const TableHeading = () => (
  <>
    {["", "Name", "Published", "Base Price", "Active", ""].map((heading, index) => (
      <th key={index} scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {heading}
      </th>
    ))}
  </>
);

export const VisitTypesMainPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const visitTypesQuery = useVisitTypes();
  const reorderVisitTypes = useReorderVisitTypes();
  const setVisitTypeActive = useSetVisitTypeActive();
  const deleteVisitType = useDeleteVisitType();
  const visitTypes = visitTypesQuery.data || [];
  const [apiError, setApiError] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);

  // Reorder is local-only until "Save Order" is pressed — no API hit per drag.
  const onDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    const reordered = [...visitTypes];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    queryClient.setQueryData(visitTypeKeys.all, reordered);
    setOrderDirty(true);
  };

  const saveOrder = () => {
    reorderVisitTypes.mutate(
      visitTypes.map((visitType) => visitType.id),
      {
        onSuccess: () => setOrderDirty(false),
        onError: (err) => {
          setApiError(errorFormatter(err?.response?.data?.errors || err?.message));
        },
      },
    );
  };

  // Optimistic: flip immediately, revert if the API rejects (e.g. unpublished type).
  const toggleActive = (visitType) => {
    const nextActive = !visitType.active;
    queryClient.setQueryData(visitTypeKeys.all, (current = []) =>
      current.map((entry) => (entry.id === visitType.id ? { ...entry, active: nextActive } : entry)),
    );
    setVisitTypeActive.mutate(
      { id: visitType.id, active: nextActive },
      {
        onError: (err) => {
          queryClient.setQueryData(visitTypeKeys.all, (current = []) =>
            current.map((entry) => (entry.id === visitType.id ? { ...entry, active: visitType.active } : entry)),
          );
          setApiError(errorFormatter(err?.response?.data?.errors || err?.message));
        },
      },
    );
  };

  const confirmDelete = () => {
    const targetId = deleteTarget.id;
    deleteVisitType.mutate(targetId, {
      onSuccess: () => {
        setDeleteTarget(null);
        queryClient.setQueryData(visitTypeKeys.all, (current = []) => current.filter((entry) => entry.id !== targetId));
      },
      onError: (err) => {
        setDeleteTarget(null);
        setApiError(errorFormatter(err?.response?.data?.errors || err?.message));
      },
    });
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Visit Types</h1>
            <p className="text-sm text-gray-500">
              Drag rows to change the order patients see. Types must be published before they can be activated.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {orderDirty && (
              <Button
                btnText="Save Order"
                variant="Secondary"
                className="h-10 px-4"
                isLoading={reorderVisitTypes.isLoading}
                isDisabled={reorderVisitTypes.isLoading}
                onClick={saveOrder}
              />
            )}
            <Button btnText="Add Visit Type" className="h-10 px-4" onClick={() => setAddModalOpen(true)} />
          </div>
        </div>

        {(apiError || visitTypesQuery.error) && (
          <ApiError
            error={apiError || errorFormatter(visitTypesQuery.error?.response?.data?.errors || visitTypesQuery.error?.message)}
            setApiError={setApiError}
          />
        )}

        {visitTypesQuery.isLoading ? (
          <Spinner />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="align-middle inline-block min-w-full shadow border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <TableHeading />
                  </tr>
                </thead>
                <Droppable droppableId="visit-types">
                  {(provided) => (
                    <tbody ref={provided.innerRef} {...provided.droppableProps} className="bg-white divide-y divide-gray-200">
                      {visitTypes.map((visitType, index) => (
                        <Draggable key={visitType.id} draggableId={visitType.id} index={index}>
                          {(draggableProvided) => (
                            <tr ref={draggableProvided.innerRef} {...draggableProvided.draggableProps} className="bg-white">
                              <td className="px-4 py-3 w-10" {...draggableProvided.dragHandleProps}>
                                <BarsIcon className="h-4 w-4 text-gray-400 cursor-grab" />
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  {visitType.iconUrl && <img src={visitType.iconUrl} alt="" className="h-8 w-8 rounded object-cover" />}
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {visitType.name}
                                    </div>
                                    <div className="text-xs text-gray-500">{visitType.slug}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {visitType.publishedVersion > 0 ? (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                                    v{visitType.publishedVersion}
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Draft only</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {visitType.resolvedCheckout?.base ? `$${visitType.resolvedCheckout.base.price}` : "—"}
                              </td>
                              <td className="px-4 py-3">
                                <Toggle enabled={!!visitType.active} onChange={() => toggleActive(visitType)} />
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    btnText="Edit"
                                    variant="Secondary"
                                    className="h-8 px-2"
                                    onClick={() => history.push(`${VISIT_TYPES}/editor/${visitType.id}`)}
                                  />
                                  <Button
                                    btnText="Delete"
                                    variant="Delete"
                                    className="h-8 px-2"
                                    onClick={() => setDeleteTarget(visitType)}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {visitTypes.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                            No visit types yet — add one to get started.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </div>
          </DragDropContext>
        )}
      </div>

      {addModalOpen && (
        <AddVisitTypeModal
          open={addModalOpen}
          setOpen={setAddModalOpen}
          onCreated={(id) => {
            setAddModalOpen(false);
            console.log(id, "ID");
            history.push(`${VISIT_TYPES}/editor/${id}`);
          }}
        />
      )}

      {deleteTarget && (
        <ModalBasic
          open={!!deleteTarget}
          heading={`Delete "${deleteTarget.name}"?`}
          content="This hides the visit type everywhere. Existing visits and published versions are kept."
          btnText="Delete"
          loading={deleteVisitType.isLoading}
          setOpen={(value) => {
            if (value === "Delete") {
              confirmDelete();
            } else {
              setDeleteTarget(null);
            }
          }}
        />
      )}
    </MainLayout>
  );
};
